import React from "react";
import MarketingLayout from "../components/MarketingLayout";
import SEO from "../components/SEO";

export default function TermsOfServicePage() {
  return (
    <MarketingLayout>
      <SEO
        title="Terms of Service"
        path="/terms"
        description="Review the terms and conditions for using the Eval Ninja player evaluation platform."
      />
      <section className="bg-gradient-to-br from-slate-50 via-white to-sky-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h1 className="text-4xl font-bold text-gray-900 tracking-tight">
            Terms of Service
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Last updated: February 11, 2026
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-gray prose-headings:font-semibold prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600 prose-a:text-sky-600">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using the Eval Ninja web application and related
            services (the "Service"), you agree to be bound by these Terms of
            Service ("Terms"). If you do not agree to these Terms, do not use
            the Service.
          </p>
          <p>
            If you are using the Service on behalf of an organization (such as a
            sports club or league), you represent that you have the authority to
            bind that organization to these Terms.
          </p>

          <h2>2. Description of Service</h2>
          <p>
            Eval Ninja is a web-based platform that enables sports clubs and
            organizations to conduct player evaluations, track skill
            development, generate player rankings, and distribute evaluation
            reports to parents and guardians.
          </p>

          <h2>3. Accounts and Access</h2>
          <ul>
            <li>
              You must provide accurate and complete information when creating an
              account.
            </li>
            <li>
              You are responsible for maintaining the confidentiality of your
              login credentials and for all activity that occurs under your
              account.
            </li>
            <li>
              Club administrators are responsible for managing staff access,
              including inviting and removing users and assigning appropriate
              roles.
            </li>
            <li>
              You must notify us immediately if you become aware of any
              unauthorized use of your account.
            </li>
          </ul>

          <h2>4. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>
              Use the Service for any unlawful purpose or in violation of any
              applicable laws or regulations.
            </li>
            <li>
              Upload, transmit, or distribute any content that is harmful,
              offensive, defamatory, or infringes on the rights of others.
            </li>
            <li>
              Attempt to gain unauthorized access to any part of the Service,
              other accounts, or systems connected to the Service.
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the
              Service.
            </li>
            <li>
              Use the Service to collect, store, or process personal information
              about individuals without their knowledge or appropriate consent.
            </li>
            <li>
              Reverse-engineer, decompile, or disassemble any part of the
              Service.
            </li>
          </ul>

          <h2>5. Your Content and Data</h2>
          <ul>
            <li>
              You retain ownership of all data you enter into the Service,
              including player information, evaluation scores, and comments
              ("Your Content").
            </li>
            <li>
              You grant us a limited license to use, store, and process Your
              Content solely for the purpose of providing and improving the
              Service.
            </li>
            <li>
              You are responsible for ensuring you have the necessary rights and
              permissions to enter player and parent data into the Service,
              including compliance with applicable privacy laws.
            </li>
            <li>
              Club administrators may export or delete their club's data at any
              time through the Service.
            </li>
          </ul>

          <h2>6. Parent Reports</h2>
          <p>
            When a club administrator sends evaluation reports to parents, each
            report is delivered via a secure, token-based link. Parents do not
            need to create an account to view reports. Club administrators are
            responsible for ensuring parent email addresses are accurate and that
            they have appropriate authorization to share evaluation data with
            parents.
          </p>

          <h2>7. Subscription and Payment</h2>
          <ul>
            <li>
              Some features of the Service require a paid subscription. Pricing
              details are available on our Pricing page.
            </li>
            <li>
              Subscriptions are billed on a monthly basis unless otherwise
              specified.
            </li>
            <li>
              You may upgrade, downgrade, or cancel your subscription at any
              time. Changes take effect at the start of the next billing cycle.
            </li>
            <li>
              We reserve the right to change pricing with 30 days' notice.
              Continued use after a price change constitutes acceptance of the
              new pricing.
            </li>
            <li>
              Refunds are handled on a case-by-case basis. Contact support for
              refund requests.
            </li>
          </ul>

          <h2>8. Service Availability</h2>
          <p>
            We strive to maintain high availability of the Service, but we do
            not guarantee uninterrupted or error-free operation. We may
            temporarily suspend the Service for maintenance, updates, or
            circumstances beyond our control. We will make reasonable efforts to
            provide advance notice of planned downtime.
          </p>

          <h2>9. Intellectual Property</h2>
          <p>
            The Service, including its design, code, features, and branding, is
            owned by Eval Ninja and protected by intellectual property laws. You
            may not copy, modify, distribute, or create derivative works based
            on the Service without our prior written consent.
          </p>

          <h2>10. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Eval Ninja and its
            officers, directors, employees, and agents shall not be liable for
            any indirect, incidental, special, consequential, or punitive
            damages, including but not limited to loss of data, revenue, or
            profits, arising out of or related to your use of the Service.
          </p>
          <p>
            Our total liability for any claim arising from or related to the
            Service shall not exceed the amount you paid us in the 12 months
            preceding the claim.
          </p>

          <h2>11. Disclaimer of Warranties</h2>
          <p>
            The Service is provided "as is" and "as available" without
            warranties of any kind, whether express or implied, including but
            not limited to implied warranties of merchantability, fitness for a
            particular purpose, and non-infringement.
          </p>
          <p>
            We do not warrant that the Service will meet your specific
            requirements, that evaluation data will be error-free, or that team
            placement decisions based on Service data will produce optimal
            results.
          </p>

          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Eval Ninja and its
            officers, directors, employees, and agents from any claims,
            liabilities, damages, losses, or expenses (including reasonable
            attorneys' fees) arising from your use of the Service, your
            violation of these Terms, or your violation of any rights of a third
            party.
          </p>

          <h2>13. Termination</h2>
          <ul>
            <li>
              You may stop using the Service and request account deletion at any
              time by contacting support.
            </li>
            <li>
              We may suspend or terminate your access to the Service if you
              violate these Terms or engage in conduct that we reasonably
              believe is harmful to other users or the Service.
            </li>
            <li>
              Upon termination, your right to use the Service ceases
              immediately. We will retain your data for 30 days after
              termination to allow for data export, after which it may be
              permanently deleted.
            </li>
          </ul>

          <h2>14. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of the United States and the state in which Eval Ninja is
            incorporated, without regard to conflict of law principles.
          </p>

          <h2>15. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of
            material changes by posting the updated Terms on this page and
            updating the "Last updated" date. Your continued use of the Service
            after changes are posted constitutes acceptance of the revised
            Terms.
          </p>

          <h2>16. Contact Us</h2>
          <p>
            If you have questions about these Terms, please contact us at:
          </p>
          <p>
            <strong>Eval Ninja</strong>
            <br />
            Email: support@evalninja.com
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
